import {
  Button,
  Group,
  Modal,
  Stack,
  TextInput,
  UnstyledButton,
  Text,
  rem,
} from '@mantine/core';
import { useState } from 'react';
import { TbListSearch } from 'react-icons/tb';
import { List } from '@/components/List/List';
import { useNavigate } from 'react-router-dom';
import { createKey } from '@/api/params';
import useSWR from 'swr';
import { getFetcher } from '@/api/fetchers';
import { dateFormatUI } from '@/index';
import dayjs from 'dayjs';
import { RadioButton } from './RadioButton';
import { notifications } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';
import { SortHeader } from '@/components/List/Header/SortHeader';
import { TextHeader } from '@/components/List/Header/TextHeader';
import { ClampedTextCell } from '@/components/List/Cell/ClampedTextCell';
import { AlignedCell } from '@/components/List/Cell/AlignedCell';
import { type ClientsRequest } from '@/schemas/API/GET/client/request';
import { type Client, type Clients } from '@/schemas/API/GET/client/response';

const ClientNameCell = (client: Client) => (
  <ClampedTextCell>
    {client.lastName} {client.firstName}
  </ClampedTextCell>
);

const ClientAddressCell = (client: Client) => (
  <ClampedTextCell>
    {client.permAddress.street} {client.permAddress.descNum}
    {client.permAddress.orientNum !== 0 && '/' + client.permAddress.orientNum}, {client.permAddress.city}{' '}
    {client.permAddress.postCode}
  </ClampedTextCell>
);

const ClientBirthdateCell = (client: Client) => (
  <Text>{dayjs(client.birthdate).format(dateFormatUI)}</Text>
);

const ClientSelectCell = (selected: number | undefined, setSelected: (value: React.SetStateAction<number | undefined>) => void) => {
  const Cell = (client: Client) => (
    <AlignedCell align="center">
      <RadioButton
        selected={selected === client.id}
        onClick={() => setSelected(client.id)}
        textChecked="Vybrán"
        textUnchecked="Vybrat"
      />
    </AlignedCell>
  );
  return Cell;
}

interface ListClinetsModalProps {
  opened: boolean;
  close: () => void;
  parrentClose: () => void;
}

export const ListClientsModal: React.FC<ListClinetsModalProps> = props => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<number>();
  const [requestParams, setRequestParams] = useState<ClientsRequest>({
    serv: 1,
    page: 1,
    size: 5,
  });

  const { data: clients } = useSWR<Clients>(
    '/clients' + createKey(requestParams),
    getFetcher
  );

  const setRequestParam = <K extends keyof ClientsRequest>(
    fieldName: K,
    value: ClientsRequest[K] | null
  ): void => {
    setRequestParams(prevParams => {
      return { ...prevParams, [fieldName]: value };
    });
  };

  const handleClose = () => {
    props.close();
    props.parrentClose();
  };

  return (
    <Modal
      opened={props.opened}
      onClose={handleClose}
      title="Výběr existujícího klienta"
      centered
      size="xl"
    >
      <Stack className="h-full">
        <Group>
          <TextInput
            placeholder="Vyhledat klienta"
            onChange={e => {
              setRequestParam('query', e.currentTarget.value);
            }}
            className="flex-1"
          />
          <UnstyledButton data-testid="search-button">
            <TbListSearch size={25} />
          </UnstyledButton>
        </Group>

        <div className="flex-1">
          <List
            loading={clients === undefined}
            pagination={{
              page: Number(requestParams.page),
              setPage: e => setRequestParam('page', e),
              pageTotal: clients?.totalPages ?? 1,
            }}
            columns={[
              <SortHeader
                key={1}
                label="Příjmení a jméno"
                fields={['firstName', 'lastName']}
                callback={sort => setRequestParam('sort', sort)}
                currentSort={requestParams.sort ?? []}
              />,
              <TextHeader key={2}>Bydliště</TextHeader>,
              <TextHeader key={3}>Datum narození</TextHeader>,
              <TextHeader key={4} align="center">
                Výběr
              </TextHeader>,
            ]}
            content={{
              items: clients?.content ?? [],
              onClick: () => {},
              renders: [
                ClientNameCell,
                ClientAddressCell,
                ClientBirthdateCell,
                ClientSelectCell(selected, setSelected)
              ],
            }}
          />
        </div>

        <Group justify="space-between" grow>
          <Button variant="outline" onClick={handleClose}>
            Zrušit
          </Button>

          <Button
            onClick={() => {
              if (!selected) {
                notifications.show({
                  message: `Nebyl vybrán žádný klient`,
                  color: 'red',
                  icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
                });
                return;
              }
              navigate(`/applicants/new-client-form/${selected}`);
            }}
          >
            Pokračovat
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
